import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';

/** Shared form primitives for the proposal editor's section forms. Every
 *  input takes the standard `disabled` prop so the whole form can be locked
 *  in one pass (signed proposals, an active save conflict). */

export function SectionCard({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="editor-section rounded-2xl border border-line bg-white p-6">
      <h2 className="font-display text-xl text-ink">{title}</h2>
      {hint && <p className="mt-1.5 text-sm text-ink-faint">{hint}</p>}
      <div className="mt-5 space-y-5">{children}</div>
    </section>
  );
}

export function Field({
  label,
  hint,
  error,
  full,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string;
  full?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={`block space-y-1.5 ${full ? 'col-span-full' : ''}`}>
      <span className="text-sm font-medium text-ink">{label}</span>
      {children}
      {hint && <span className="block text-xs text-ink-faint">{hint}</span>}
      {error && <span className="block text-xs text-error">{error}</span>}
    </label>
  );
}

const baseInputClasses =
  'w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-sm text-ink outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/15 disabled:bg-paper-2 disabled:text-ink-faint';

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${baseInputClasses} ${props.className ?? ''}`} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${baseInputClasses} ${props.className ?? ''}`} />;
}

export function NumberInput(
  props: Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'value'> & {
    value: number | '';
    onChange: (value: number | '') => void;
  },
) {
  const { value, onChange, ...rest } = props;
  return (
    <TextInput
      {...rest}
      type="number"
      inputMode="numeric"
      value={value}
      onChange={(event) => {
        const raw = event.target.value;
        onChange(raw === '' ? '' : Number(raw));
      }}
    />
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5 text-sm font-medium text-ink">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        disabled={disabled}
        className="h-4 w-4 accent-accent"
      />
      {label}
    </label>
  );
}

export function RemoveButton({ onClick, disabled, label = 'Remove' }: { onClick: () => void; disabled?: boolean; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="text-xs font-medium text-ink-faint transition hover:text-error disabled:opacity-40"
    >
      {label}
    </button>
  );
}

export function AddButton({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rounded-full border border-accent px-4 py-1.5 text-sm font-medium text-accent transition hover:bg-accent-tint disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}

export function ItemCard({ children, onRemove, disabled }: { children: ReactNode; onRemove?: () => void; disabled?: boolean }) {
  return (
    <div className="rounded-xl border border-line bg-paper p-4">
      <div className="space-y-3">{children}</div>
      {onRemove && (
        <div className="mt-3 flex justify-end">
          <RemoveButton onClick={onRemove} disabled={disabled} />
        </div>
      )}
    </div>
  );
}

export function WarningNote({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-lg border border-warning/30 bg-warning-tint px-3.5 py-2.5 text-sm text-warning">
      {children}
    </p>
  );
}
